import type { InputHTMLAttributes, ReactNode, Ref } from 'react';
import { MOTION_COLORS } from '../internal/classes';
import { cx } from '../internal/cx';

/** Field height, following the density step in `data-density`. */
export type InputSize = 'sm' | 'md' | 'lg';

const SIZE_CLASSES: Readonly<Record<InputSize, string>> = {
  sm: 'h-(--ui-control-sm) text-caption',
  md: 'h-(--ui-control-md) text-subhead',
  lg: 'h-(--ui-control-lg) text-body',
};

/**
 * The focus ring sits on the box rather than on the `<input>`, so an adornment inside the box is
 * enclosed by it. Text fields are always "focus visible", so a click shows the ring too. It is drawn
 * inside the box, over the border: a field usually fills the width of a scrolling container (a
 * dialog body, a settings page), and that container clips whatever is painted outside it.
 */
const BOX = cx(
  'flex w-full items-center gap-2 rounded-sm border bg-surface px-2 text-text',
  // The typing caret's ring, not a button's: a button inside the box draws its own.
  'has-[input:focus-visible]:outline-2 has-[input:focus-visible]:-outline-offset-2',
  'has-[input:focus-visible]:outline-focus-ring',
  'has-[input:disabled]:cursor-not-allowed has-[input:disabled]:opacity-50',
  MOTION_COLORS,
);

/** Props of {@link Input}. */
export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  /** Field height; `md` by default. */
  size?: InputSize;
  /** Draws the error border and sets `aria-invalid`. {@link TextField} sets it from its `error`. */
  invalid?: boolean;
  /** Element before the text, e.g. a search glyph. Decorative: give it no accessible name. */
  startAdornment?: ReactNode;
  /** Element after the text, e.g. a clear button or a unit. */
  endAdornment?: ReactNode;
  /** Extra utility classes for the field box (not for the `<input>` itself). */
  className?: string;
  /** Ref to the `<input>`. */
  ref?: Ref<HTMLInputElement>;
}

/**
 * A single-line text field without a label. Use {@link TextField} unless the field already has a
 * visible label elsewhere (a search box in a toolbar, a cell editor); a bare `Input` still needs an
 * `aria-label` in that case.
 *
 * @example <Input size="sm" startAdornment={<Icon name="search" />} aria-label={t('search.label')} />
 */
export function Input({
  size = 'md',
  invalid = false,
  startAdornment,
  endAdornment,
  className,
  ...rest
}: InputProps) {
  return (
    <div
      className={cx(
        BOX,
        SIZE_CLASSES[size],
        invalid ? 'border-danger' : 'border-border-strong',
        className,
      )}
    >
      {startAdornment !== undefined && (
        <span aria-hidden="true" className="flex shrink-0 items-center text-text-tertiary">
          {startAdornment}
        </span>
      )}
      <input
        className={cx(
          'min-w-0 flex-1 bg-transparent py-0 text-inherit outline-none',
          'placeholder:text-text-tertiary disabled:cursor-not-allowed',
        )}
        aria-invalid={invalid || undefined}
        {...rest}
      />
      {endAdornment !== undefined && (
        <span className="flex shrink-0 items-center text-text-tertiary">{endAdornment}</span>
      )}
    </div>
  );
}
